/**
 * Student Dashboard
 *
 * Displays assigned experiments, lab history, progress overview, and quick actions.
 * Designed for instrument-panel aesthetic with dense information display.
 */

import { Link } from 'react-router-dom';
import styles from './StudentDashboard.module.css';

// Domain types for experiments
export type ExperimentDomain = 'physics' | 'chemistry' | 'biology';

// Assignment status tracking
export type AssignmentStatus = 'not_started' | 'in_progress' | 'completed';

// Priority level for assignments
export type AssignmentPriority = 'high' | 'medium' | 'low';

// Experiment assignment from teacher
export interface ExperimentAssignment {
  id: string;
  experimentId: string;
  experimentSlug: string;
  domain: ExperimentDomain;
  name: string;
  assignedBy: string;
  assignedAt: Date;
  dueDate: Date | null;
  status: AssignmentStatus;
  priority: AssignmentPriority;
  progress: number; // 0-100
}

// Completed lab record
export interface LabRecord {
  id: string;
  experimentId: string;
  experimentSlug: string;
  domain: ExperimentDomain;
  name: string;
  completedAt: Date;
  score: number; // 0-100
  attempts: number;
}

// Progress data by topic
export interface TopicProgress {
  topic: string;
  domain: ExperimentDomain;
  masteryScore: number; // 0-100
  completedCount: number;
  totalCount: number;
  averageScore: number;
}

// Quick action data
export interface QuickAction {
  id: string;
  label: string;
  title: string;
  icon: React.ReactNode;
  disabled: boolean;
  meta?: string;
  to?: string;
  onClick?: () => void;
}

export interface StudentDashboardProps {
  // Data props - all optional for empty states
  assignments?: ExperimentAssignment[];
  labHistory?: LabRecord[];
  progress?: TopicProgress[];
  quickActions?: QuickAction[];
}

export function StudentDashboard({
  assignments = [],
  labHistory = [],
  progress = [],
  quickActions = [],
}: StudentDashboardProps) {

  // Determine if dashboard has any data
  const hasData = assignments.length > 0 || labHistory.length > 0 || progress.length > 0;

  // Format date for display
  const formatDate = (date: Date | null): string => {
    if (!date) return 'No due date';
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (days < 0) return 'Overdue';
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    if (days <= 7) return `Due in ${days} days`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get due date class
  const getDueDateClass = (date: Date | null): string => {
    if (!date) return '';
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (days < 0) return styles.overdue ?? 'overdue';
    if (days <= 2) return styles.dueSoon ?? 'dueSoon';
    return '';
  };

  // Get status badge class
  const getStatusClass = (status: AssignmentStatus): string => {
    switch (status) {
      case 'not_started': return styles.notStarted ?? 'notStarted';
      case 'in_progress': return styles.inProgress ?? 'inProgress';
      case 'completed': return styles.completed ?? 'completed';
      default: return '';
    }
  };

  // Get progress fill class
  const getProgressClass = (score: number): string => {
    if (score < 40) return styles.low ?? 'low';
    if (score < 70) return styles.medium ?? 'medium';
    return styles.high ?? 'high';
  };

  // Default quick actions
  const defaultQuickActions: QuickAction[] = [
    {
      id: 'continue',
      label: 'Continue',
      title: 'Last Experiment',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      disabled: true,
      meta: 'No recent activity',
    },
    {
      id: 'results',
      label: 'View Results',
      title: 'Lab Results',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      disabled: labHistory.length === 0,
      meta: labHistory.length > 0 ? `${labHistory.length} completed` : 'No completed labs',
    },
    {
      id: 'browse',
      label: 'Browse',
      title: 'Experiment Catalog',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      disabled: false,
      to: '/experiments',
    },
    {
      id: 'help',
      label: 'Get Help',
      title: 'Lab Guide',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      disabled: false,
    },
  ];

  const actions = quickActions.length > 0 ? quickActions : defaultQuickActions;

  return (
    <div className={styles.container}>
      <main className={styles.main}>
        {/* Header */}
        <header className={styles.header}>
          <h1 className={styles.headerTitle}>Student Dashboard</h1>
          <p className={styles.headerSubtitle}>
            Track your assignments, review completed labs, and monitor progress
          </p>
        </header>

        {/* Global Empty State */}
        {!hasData && (
          <div className={styles.emptyState}>
            <svg className={styles.emptyStateIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h2 className={styles.emptyStateTitle}>No Activity Yet</h2>
            <p className={styles.emptyStateText}>
              When your teacher assigns experiments or you complete labs, your data will appear here.
            </p>
          </div>
        )}

        {hasData && (
          <div className={styles.dashboardGrid}>
            {/* Quick Actions */}
            <section className={styles.quickActions}>
              {actions.map((action) => {
                if (action.to && !action.disabled) {
                  return (
                    <Link
                      key={action.id}
                      to={action.to}
                      className={styles.actionCard}
                    >
                      <div className={styles.actionCardHeader}>
                        <div className={styles.actionCardIcon}>{action.icon}</div>
                        <h3 className={styles.actionCardTitle}>{action.title}</h3>
                      </div>
                      <p className={styles.actionCardLabel}>{action.label}</p>
                      {action.meta && <p className={styles.actionCardMeta}>{action.meta}</p>}
                    </Link>
                  );
                }
                return (
                  <button
                    key={action.id}
                    className={styles.actionCard}
                    disabled={action.disabled}
                    onClick={action.onClick}
                  >
                    <div className={styles.actionCardHeader}>
                      <div className={styles.actionCardIcon}>{action.icon}</div>
                      <h3 className={styles.actionCardTitle}>{action.title}</h3>
                    </div>
                    <p className={styles.actionCardLabel}>{action.label}</p>
                    {action.meta && <p className={styles.actionCardMeta}>{action.meta}</p>}
                  </button>
                );
              })}
            </section>

            {/* Assigned Experiments */}
            <section className={`${styles.card} ${styles.assignedExperiments}`}>
              <div className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>Assigned Experiments</h2>
                {assignments.length > 0 && (
                  <Link to="/assignments" className={styles.cardAction}>View All</Link>
                )}
              </div>
              {assignments.length === 0 ? (
                <div className={styles.cardEmptyState}>
                  <svg className={styles.cardEmptyStateIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p className={styles.cardEmptyStateText}>No pending assignments</p>
                </div>
              ) : (
                <div className={styles.experimentList}>
                  {assignments.map((assignment) => (
                    <Link
                      key={assignment.id}
                      to={`/experiments/${assignment.domain}/${assignment.experimentSlug}`}
                      className={`${styles.experimentItem} ${styles[`priority${assignment.priority.charAt(0).toUpperCase()}${assignment.priority.slice(1)}`]}`}
                    >
                      <div className={styles.experimentInfo}>
                        <h3 className={styles.experimentName}>{assignment.name}</h3>
                        <div className={styles.experimentMeta}>
                          <span className={styles.experimentMetaItem}>
                            {assignment.domain.toUpperCase()}
                          </span>
                          <span className={styles.experimentMetaItem}>
                            by {assignment.assignedBy}
                          </span>
                        </div>
                      </div>
                      <div className={styles.experimentStatus}>
                        <span className={`${styles.statusBadge} ${getStatusClass(assignment.status)}`}>
                          {assignment.status.replace('_', ' ')}
                        </span>
                        <span className={`${styles.dueDate} ${getDueDateClass(assignment.dueDate)}`}>
                          {formatDate(assignment.dueDate)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            {/* Lab History */}
            <section className={`${styles.card} ${styles.labHistory}`}>
              <div className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>Lab History</h2>
                {labHistory.length > 0 && (
                  <Link to="/history" className={styles.cardAction}>View All</Link>
                )}
              </div>
              {labHistory.length === 0 ? (
                <div className={styles.cardEmptyState}>
                  <svg className={styles.cardEmptyStateIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className={styles.cardEmptyStateText}>No completed labs yet</p>
                </div>
              ) : (
                <div className={styles.historyList}>
                  {labHistory.map((record) => (
                    <Link
                      key={record.id}
                      to={`/results/${record.id}`}
                      className={styles.historyItem}
                    >
                      <svg className={styles.historyIcon} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div className={styles.historyInfo}>
                        <h4 className={styles.historyName}>{record.name}</h4>
                        <p className={styles.historyDate}>
                          {record.completedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                      <span className={styles.historyScore}>{record.score}%</span>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            {/* Progress Overview */}
            {progress.length > 0 && (
              <section className={`${styles.card} ${styles.progressOverview}`}>
                <div className={styles.cardHeader}>
                  <h2 className={styles.cardTitle}>Progress by Topic</h2>
                </div>
                <div className={styles.progressGrid}>
                  {progress.map((topic) => (
                    <div key={topic.topic} className={styles.progressTopic}>
                      <div className={styles.topicHeader}>
                        <h4 className={styles.topicName}>{topic.topic}</h4>
                        <span className={styles.topicScore}>{topic.masteryScore}%</span>
                      </div>
                      <div className={styles.progressBar}>
                        <div
                          className={`${styles.progressFill} ${getProgressClass(topic.masteryScore)}`}
                          style={{ width: `${topic.masteryScore}%` }}
                        />
                      </div>
                      <div className={styles.topicStats}>
                        <span className={styles.topicStat}>{topic.completedCount}/{topic.totalCount} completed</span>
                        <span className={styles.topicStat}>Avg: {topic.averageScore}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
